import { coreAiService } from "./coreService";
import { AiResponse } from "./types";

// Metadata service for tags, pull quotes, and research assistance
export const metadataService = {
  async generateTags(content: string, title: string, existingTags: string[] = []): Promise<AiResponse> {
    const systemPrompt = `You are a content categorization specialist with expertise in Named Entity Recognition.
    Analyze the article to identify key topics, entities, and themes for tagging.
    
    CRITICAL INSTRUCTIONS FOR TAG GENERATION:
    
    1. PRIMARY SUBJECT IDENTIFICATION - MOST IMPORTANT STEP:
       - If the article is about a specific person (e.g., Donald Trump, Elon Musk), their full name MUST be the first high-level tag
       - If about an organization or country (e.g., United Nations, Ukraine), it MUST be the first high-level tag
       - If about an event or concept (e.g., Climate Change, Olympics), it MUST be the first high-level tag
       - Check the article title and first paragraph carefully to identify the primary subject
    
    2. Then add 1-2 additional high-level category tags that represent broad topics
       Examples:
       - "Politics" for political articles
       - "Climate Change" for environmental articles
       - "Technology" for tech-related content
    
    3. Finally, add more specific tags related to the content
    
    EXAMPLES:
    - For an article about Trump's campaign: "Donald Trump" (high-level), "U.S. Politics" (high-level), "Presidential Election" (specific)
    - For an article about climate in the Arctic: "Climate Change" (high-level), "Arctic" (specific), "Global Warming" (specific)
    
    Ensure named entities from the title ALWAYS appear as tags with the highest relevance.
    Tags should follow a hierarchical structure - from general to specific.
    
    Format as JSON with an array of objects, each containing:
    - 'tag': The tag text
    - 'relevance': Numeric score (1-10) of the tag's relevance (PRIMARY SUBJECTS must get 10)
    - 'level': Either 'high' (for primary subjects and broad category tags) or 'specific' (for more detailed tags)`;

    const prompt = `Generate relevant tags for this article:
    Title: ${title}
    Existing tags: ${existingTags.join(', ')}
    Content: ${content.substring(0, 2500)}${content.length > 2500 ? '...' : ''}`;

    return coreAiService.generateContent({
      systemPrompt,
      prompt,
      temperature: 0.4, // Lower temperature for more consistent results
      maxTokens: 800,
      model: 'gpt-4o-mini',
      content,
      title
    });
  },

  async findPullQuotes(content: string): Promise<AiResponse> {
    const systemPrompt = `You are an editor with expertise in identifying impactful quotes.
    Find 3-5 potential pull quotes from the article that are memorable, shareable, and impactful.
    Select quotes that represent key points, emotional moments, or surprising insights.
    Each quote should be 10-25 words and stand on its own without additional context.
    Format as JSON with an array of objects, each containing 'quote' and 'impact' (1-10) properties.`;

    const prompt = `Find the most impactful potential pull quotes from this article:
    ${content.substring(0, 5000)}${content.length > 5000 ? '...' : ''}`;

    return coreAiService.generateContent({
      systemPrompt,
      prompt,
      temperature: 0.6,
      maxTokens: 1000,
      model: 'gpt-4o-mini',
      content
    });
  },

  async researchTopic(query: string, context: string): Promise<AiResponse> {
    const systemPrompt = `You are a research assistant for journalists.
    Provide concise, factual information related to the specified query.
    Include definitions, historical context, related concepts, and relevant statistics when available.
    Suggest credible sources for further reading.
    Format as JSON with 'summary', 'details', 'related_concepts', and 'sources' properties.`;

    const prompt = `Research the following topic in the context of this article:
    Query: ${query}
    Article context: ${context.substring(0, 2000)}${context.length > 2000 ? '...' : ''}`;

    return coreAiService.generateContent({
      systemPrompt,
      prompt,
      temperature: 0.3,
      maxTokens: 1200,
      model: 'gpt-4o-mini',
      content: context
    });
  }
};
